import { useCallback, useState } from 'react';

import { useFlags } from '@feathr/extender/state';

import type { TChannel, TCreativeType } from '../types';

// =============================================================================
// Modal State Types
// =============================================================================

/** No modal is open */
type TModalStateNone = { modal: 'none' };

/** AddCreativeFlow modal is open */
type TModalStateAddCreativeFlow = {
  modal: 'addCreativeFlow';
  returnToStep2?: boolean;
  channel?: TChannel;
};

/** DuplicateCreativeModal is open */
type TModalStateDuplicateCreative = {
  modal: 'duplicateCreative';
  channel: TChannel;
};

/** AddImageVideoFlow is open (for either meta or display channel) */
type TModalStateImageVideo = {
  modal: 'imageVideo';
  channel: TChannel;
};

/** AddBannersnackBuildFlow is open */
type TModalStateBannersnackBuild = {
  modal: 'bannersnackBuild';
  channel: TChannel;
};

/** AddAdTagCreative is open */
type TModalStateAdTagCreative = {
  modal: 'adTagCreative';
  channel: 'display';
};

/** Internal state for managing which modal is currently displayed */
export type TModalState =
  | TModalStateNone
  | TModalStateAddCreativeFlow
  | TModalStateDuplicateCreative
  | TModalStateImageVideo
  | TModalStateBannersnackBuild
  | TModalStateAdTagCreative;

// =============================================================================
// Hook Parameters and Return Types
// =============================================================================

export interface IUseAddCreativeButtonParams {
  /** Callback when the creative flow completes (for non-modal flows) */
  onComplete?: (channel: TChannel, creativeType: TCreativeType) => void;
}

export interface IUseAddCreativeButtonReturn {
  /** Current modal state */
  modalState: TModalState;
  /** Whether the AddCreativeFlow modal is open */
  isAddCreativeFlowOpen: boolean;
  /** Whether the DuplicateCreativeModal is open */
  isDuplicateModalOpen: boolean;
  /** Whether the AddImageVideoFlow is open */
  isImageVideoOpen: boolean;
  /** Channel for the AddImageVideoFlow (only valid when isImageVideoOpen is true) */
  imageVideoChannel: TChannel | undefined;
  /** Whether the AddBannersnackBuildFlow is open */
  isBannersnackBuildOpen: boolean;
  /** Whether the AddAdTagCreative is open */
  isAdTagCreativeOpen: boolean;
  /** Opens the AddCreativeFlow modal */
  handleOpenAddCreativeFlow: () => void;
  /** Closes all modals */
  handleCloseAll: () => void;
  /** Handles completion of AddCreativeFlow - routes to next modal or completes */
  handleAddCreativeFlowComplete: (channel: TChannel, creativeType: TCreativeType) => void;
  /** Handles back button from DuplicateCreativeModal */
  handleDuplicateModalBack: () => void;
  /** Handles back button from AddImageVideoFlow */
  handleImageVideoBack: () => void;
  /** Handles back button from AddBannersnackBuildFlow */
  handleBannersnackBuildBack: () => void;
  /** Handles back button from AddAdTagCreative */
  handleAdTagCreativeBack: () => void;
}

// =============================================================================
// Hook Implementation
// =============================================================================

/**
 * Manages the modal state machine for the AddCreativeButton component.
 *
 * The flow supports multiple paths:
 * 1. Channel selection → Creative type → External completion (onComplete callback)
 * 2. Channel selection → Duplicate → DuplicateCreativeModal
 * 3. Channel selection → Image/Video → AddImageVideoFlow
 * 4. Channel selection → Build → AddBannersnackBuildFlow
 * 5. Channel selection → Display Ad Tag → AddAdTagCreative
 *
 * Each sub-modal can navigate back to the AddCreativeFlow at step 2.
 *
 * @param params - Hook parameters
 * @returns Modal state and handler functions
 */
export function useAddCreativeButton(
  params: IUseAddCreativeButtonParams,
): IUseAddCreativeButtonReturn {
  const { onComplete } = params;
  const { metaV2 } = useFlags();
  const [modalState, setModalState] = useState<TModalState>({ modal: 'none' });

  const handleOpenAddCreativeFlow = useCallback((): void => {
    setModalState({ modal: 'addCreativeFlow' });
  }, []);

  const handleCloseAll = useCallback((): void => {
    setModalState({ modal: 'none' });
  }, []);

  const handleAddCreativeFlowComplete = useCallback(
    (channel: TChannel, creativeType: TCreativeType): void => {
      if (creativeType === 'duplicate') {
        setModalState({ modal: 'duplicateCreative', channel });
      } else if (creativeType === 'image_video' && metaV2) {
        setModalState({ modal: 'imageVideo', channel });
      } else if (creativeType === 'build' && metaV2) {
        setModalState({ modal: 'bannersnackBuild', channel });
      } else if (channel === 'display' && creativeType === 'ad_tag' && metaV2) {
        setModalState({ modal: 'adTagCreative', channel: 'display' });
      } else {
        onComplete?.(channel, creativeType);
        setModalState({ modal: 'none' });
      }
    },
    [metaV2, onComplete],
  );

  const handleDuplicateModalBack = useCallback((): void => {
    setModalState((prev) => ({
      modal: 'addCreativeFlow',
      returnToStep2: true,
      channel: prev.modal === 'duplicateCreative' ? prev.channel : undefined,
    }));
  }, []);

  const handleImageVideoBack = useCallback((): void => {
    setModalState((prev) => ({
      modal: 'addCreativeFlow',
      returnToStep2: true,
      channel: prev.modal === 'imageVideo' ? prev.channel : undefined,
    }));
  }, []);

  const handleBannersnackBuildBack = useCallback((): void => {
    setModalState((prev) => ({
      modal: 'addCreativeFlow',
      returnToStep2: true,
      channel: prev.modal === 'bannersnackBuild' ? prev.channel : undefined,
    }));
  }, []);

  const handleAdTagCreativeBack = useCallback((): void => {
    setModalState({
      modal: 'addCreativeFlow',
      returnToStep2: true,
      channel: 'display',
    });
  }, []);

  const isAddCreativeFlowOpen = modalState.modal === 'addCreativeFlow';
  const isDuplicateModalOpen = modalState.modal === 'duplicateCreative';
  const isImageVideoOpen = modalState.modal === 'imageVideo';
  const imageVideoChannel = modalState.modal === 'imageVideo' ? modalState.channel : undefined;
  const isBannersnackBuildOpen = modalState.modal === 'bannersnackBuild';
  const isAdTagCreativeOpen = modalState.modal === 'adTagCreative';

  return {
    modalState,
    isAddCreativeFlowOpen,
    isDuplicateModalOpen,
    isImageVideoOpen,
    imageVideoChannel,
    isBannersnackBuildOpen,
    isAdTagCreativeOpen,
    handleOpenAddCreativeFlow,
    handleCloseAll,
    handleAddCreativeFlowComplete,
    handleDuplicateModalBack,
    handleImageVideoBack,
    handleBannersnackBuildBack,
    handleAdTagCreativeBack,
  };
}
